import { FC, ReactElement, useMemo } from 'react'
import { useNavigate } from 'react-router'

import { LoadingSpinner } from '../../../../../@paco/components'
import { DepartmentsList } from '../../../../../@paco/compositions'
import { ConnectedHelpTooltip } from '../../../../../@paco/connectors'
import { BaseScheduleIndex } from '../../../../../@paco/entities/BaseSchedule/BaseSchedule'
import { getUniqueArrayList } from '../../../../../@paco/helpers/array'
import trans from '../../../../../@paco/helpers/trans'
import { ScheduleRoutes } from '../../../../../@paco/routes/helpers'
import { PrivateRouteGroupRoutes } from '../../../../../@paco/routes/routes'
import { HorizontalAlignment } from '../../../../../@paco/types/alignmentTypes'

import TableActionButtons from '../../../../../components/Table/subcomponents/TableActionButtons/TableActionButtons'
import { TableAttribute } from '../../../../../components/Table/subcomponents/TableHead'
import TableContainer from '../../../../../components/Table/TableContainer'
import { translate } from '../../../../../helpers/translations/translator'

import './BaseSchedulesTable.scss'

interface BaseScheduleTableProps {
    isLoading: boolean;
    canEditBaseSchedules: boolean;
    canDeleteBaseSchedules: boolean;
    canViewBaseScheduleShifts: boolean;
    baseSchedules: BaseScheduleIndex[];
    onDeleteBaseScheduleButtonClick: (baseSchedule: BaseScheduleIndex) => void;
    onEditBaseScheduleButtonClick: (baseSchedule: BaseScheduleIndex) => void;
    className?: string;
}

const BaseSchedulesTable: FC<BaseScheduleTableProps> = ({
    isLoading,
    canEditBaseSchedules,
    canDeleteBaseSchedules,
    canViewBaseScheduleShifts,
    baseSchedules,
    onDeleteBaseScheduleButtonClick,
    onEditBaseScheduleButtonClick,
    className = '',
}): ReactElement => {
    const navigate = useNavigate()

    const handleRowClick = (baseScheduleId: string) => navigate(`${PrivateRouteGroupRoutes.shifts()}/${ScheduleRoutes.baseSchedule(baseScheduleId)}`)

    const tableAttributes: TableAttribute<BaseScheduleIndex>[] = useMemo(() => ([
        {
            label: translate('common.name'),
            key: 'name',
            className: 'base-schedules-table__row--name',
        },
        {
            label: translate('common.department(s)'),
            customAction: (baseSchedule) => {
                const departments = getUniqueArrayList(baseSchedule.departments, 'id')

                return (
                    <DepartmentsList
                        departments={departments}
                    />
                )
            },
            className: 'base-schedules-table__row--departments',
        },
        {
            label: translate('common.createdBy'),
            customAction: (baseSchedule) => (
                baseSchedule.createdBy || ''
            ),
            className: 'base-schedules-table__row--created-by',
        },
        {
            customAction: (baseSchedule) => {
                const handleOnEditButtonClick = () => onEditBaseScheduleButtonClick(baseSchedule)
                const handleOnDeleteButtonClick = () => onDeleteBaseScheduleButtonClick(baseSchedule)

                return (
                    <TableActionButtons
                        onEditButtonClick={canEditBaseSchedules ? handleOnEditButtonClick : undefined}
                        onDeleteButtonClick={canDeleteBaseSchedules ? handleOnDeleteButtonClick : undefined}
                    />
                )
            },
            className: 'base-schedules-table__row--buttons',
        },
    ]), [
        canEditBaseSchedules,
        canDeleteBaseSchedules,
    ])

    return (
        <div className={`base-schedules-table ${className}`}>
            <ConnectedHelpTooltip
                index={0}
                route="base-schedules"
                subTitle={trans('help.baseSchedules.addSchedule.title')}
                text={trans('help.baseSchedules.addSchedule.text')}
                title={trans('help.baseSchedules.title')}
                showMobileInfoWarning
                className="calendar__add-base-schedules-help-tooltip"
            />
            <ConnectedHelpTooltip
                index={1}
                route="base-schedules"
                subTitle={trans('help.baseSchedules.filters.title')}
                text={trans('help.baseSchedules.filters.text')}
                title={trans('help.baseSchedules.title')}
                showMobileInfoWarning
                className="calendar__base-schedules-filters-help-tooltip"
            />
            <ConnectedHelpTooltip
                index={2}
                route="base-schedules"
                subTitle={trans('help.baseSchedules.overview.title')}
                text={trans('help.baseSchedules.overview.text')}
                title={trans('help.baseSchedules.title')}
                className="calendar__base-schedules-overview-help-tooltip"
            />
            <ConnectedHelpTooltip
                index={3}
                route="base-schedules"
                subTitle={trans('help.baseSchedules.editBaseSchedule.title')}
                text={trans('help.baseSchedules.editBaseSchedule.text')}
                title={trans('help.baseSchedules.title')}
                showMobileInfoWarning
                alignment={HorizontalAlignment.right}
                className="calendar__edit-base-schedule-help-tooltip"
            />
            {isLoading && (
                <LoadingSpinner />
            )}

            {baseSchedules.length > 0 && (
                <TableContainer<BaseScheduleIndex>
                    attributes={tableAttributes}
                    data={baseSchedules}
                    onRowClick={canViewBaseScheduleShifts ? handleRowClick : undefined}
                    tableRowClassName="base-schedules-table__table-row"
                />
            )}
        </div>
    )
}

export default BaseSchedulesTable
